import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon } from 'lucide-react';
import type { Lang } from '../../types/funnel';
import type { LegalDocument, LegalParagraph } from '../../types/legal';
import { brand } from '../../data/siteConfig';
import { SiteFooter } from '../SiteFooter';
const back = {
  en: 'Back to website',
  es: 'Volver al sitio web'
};
export function LegalPage({
  doc,
  lang
}: {
  doc: LegalDocument;
  lang: Lang;
}) {
  useEffect(() => {
    document.title = `${doc.title} | Florida Local Movers`;
    document.documentElement.lang = lang;
    window.scrollTo(0, 0);
  }, [doc.title, lang]);
  return <div className="min-h-screen w-full bg-paper" data-id="element-1089" data-mp="3262t1ntz79d0">
      <header className="border-b border-ink-700 bg-ink" data-id="element-1090" data-mp="2x7j2190iz7ds">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 px-4 sm:px-6" data-id="element-1091" data-mp="2x4wv9blgkvwm">
          <Link to="/" className="flex min-w-0 items-center gap-2" aria-label={brand.word} data-id="element-1092" data-mp="2s9hiyo33n82b">
            <span className="rounded bg-lime px-1.5 py-0.5 font-display text-sm font-black text-ink sm:text-lg" data-id="element-1093" data-mp="2nan0xocyhnj9">{brand.mark}</span>
            <span className="truncate font-display text-base font-black tracking-tight text-white sm:text-2xl" data-id="element-1094" data-mp="2ltao6y8v77ns">{brand.word}</span>
          </Link>
          <Link to="/" className="flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-ink-600 px-3 text-sm font-semibold text-white transition-[border-color] duration-150 ease-out hover:border-ink-300" data-id="element-1095" data-mp="275frz7vnel5y">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1096" data-mp="2zqh2afin8i6o" />
            <span className="whitespace-nowrap" data-id="element-1097" data-mp="34qbxgv9gbtxj">{back[lang]}</span>
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-10 sm:px-6 lg:py-14" data-id="element-1098" data-mp="2ptuizv5ytz6r">
        <h1 className="font-display text-4xl font-black tracking-tight text-ink sm:text-5xl" data-id="element-1099" data-mp="2usfmc3gikyzf">{doc.title}</h1>
        <p className="mt-3 text-sm font-medium text-ink-500" data-id="element-1100" data-mp="1eefp5091znfv">{doc.effective}</p>
        <div className="mt-10 space-y-9" data-id="element-1101" data-mp="1jek7ywxfou99">
          {doc.sections.map(s => <section key={s.heading} data-id="element-1102" data-mp="3m0pibe8fcvco">
              <h2 className="font-display text-xl font-extrabold text-ink" data-id="element-1103" data-mp="3lznx4gxp2sh8">{s.heading}</h2>
              <div className="mt-3 space-y-3 text-base leading-relaxed text-ink-700" data-id="element-1104" data-mp="3h2xhfupsxywc">
                {s.body.map((p, i) => <Paragraph key={i} p={p} data-id="element-1105" data-mp="3c9bg2yp39hfu" data-mpi={i} />)}
              </div>
            </section>)}
        </div>
      </main>

      <SiteFooter lang={lang} showStaff={false} data-id="element-1106" data-mp="32cq4tvmh7a3k" />
    </div>;
}
function Paragraph({
  p
}: {
  p: LegalParagraph;
}) {
  if (typeof p === 'string') return <p data-id="element-1107" data-mp="2y9tne88o9k17">{p}</p>;
  if ('lines' in p) return <address className="not-italic" data-id="element-1108" data-mp="2tbs1lfjv36m1">
        {p.lines.map(l => <span key={l} className="block break-words" data-id="element-1109" data-mp="2te4swjbietbr">
            {l}
          </span>)}
      </address>;
  return <p className="break-words" data-id="element-1110" data-mp="rlgmy27hdrsa">
      {p.before}
      <Link to={p.to} className="font-semibold text-ink underline underline-offset-4" data-id="element-1111" data-mp="rk5hlxye0bz2">
        {p.linkText}
      </Link>
      {p.after}
    </p>;
}